import { ContactData } from '@/entities/ContactData';
import { reverseString } from '@/utils/reverseString';

type PhotorealPreviewType = 'Fire' | 'Office' | 'ASET Black';

export interface PerformanceMetric {
  rg: number;
  val?: number;
  d: number;
  ct: number;
  pc: number;
  global: {
    mc: number;
    hr: string;
  };
  shape: {
    mc: number;
    hr: string;
  };
}

interface QualityMetric {
  title: string;
  mc: number;
}

export class DiamondModel {
  id?: string;
  error?: string;
  sku?: string;
  carat?: number;
  shortView?: string;
  virtual?: boolean;
  labGrown?: boolean;
  cut?: {
    title: string;
    shape: Nullable<string>;
    shapeId: number;
  };

  notes?: Nullable<string>;
  media?: {
    title: string;
    url: string;
    inline: boolean;
  }[];

  previews?: {
    lighting: PhotorealPreviewType;
    url: string;
  }[];

  plottings?: Nullable<
    {
      svg: string;
      mediaSourceId: string;
    }[]
  >;

  render?: {
    cbr: Nullable<string>;
    spectrum: Nullable<string>;
  };

  clarity?: Nullable<QualityMetric>;
  color?: Nullable<QualityMetric>;
  cutQuality?: Nullable<QualityMetric>;
  polish?: Nullable<QualityMetric>;
  symmetry?: Nullable<QualityMetric>;
  fluorescence?: Nullable<QualityMetric>;
  girdleThickness?: Nullable<string>;
  certificate?: Nullable<{
    check: string;
    laboratory: string;
    number: string;
    thumb: string;
    file?: string;
  }>;

  performance?: {
    integral?: PerformanceMetric;
    integralV2?: PerformanceMetric;
    fire?: PerformanceMetric;
    brilliance?: PerformanceMetric;
    symmetry?: PerformanceMetric;
    spread?: PerformanceMetric;
  };

  type?: 'polished-diamond' | 'polished-diamond-grade' | 'cut';
  reportImg?: Nullable<{
    CrownAngleReportColor: string;
    PavilionAngleReportColor: string;
    PlotActualLengthAxisAsym: string;
    PlotActualWidthAxisAsym: string;
    PlotBothAxesSymCor: string;
    SimpleReportColor: string;
  }>;

  reportData?: Nullable<{
    crnAn: number;
    crnHeight: number;
    crnHeightPc: number;
    culet: number;
    culetPc: number;
    girdleBezel: number;
    girdleBezelPc: number;
    height: number;
    heightPc: number;
    length: number;
    pav1An: number;
    pavAn: number;
    pavDepth: number;
    pavDepthPc: number;
    rt: number;
    spread: number;
    spreadPc: number;
    table: number;
    tablePc: number;
    width: number;
  }>;

  contactData?: Nullable<ContactData>;
  fancyColor?: Nullable<{
    grade: {
      mc: number;
      title: string;
    };
    hue: string;
    modifier: null;
    short: string;
  }>;
}

export class Diamond extends DiamondModel {
  constructor(modelData: DiamondModel) {
    super();

    Object.keys(modelData).forEach((key) => {
      switch (key) {
        case 'contactData':
          if (modelData['contactData']) {
            this[key] = new ContactData(modelData['contactData']);
          }

          break;
        default:
          this[key] = modelData[key];

          break;
      }
    });
  }

  get productId() {
    return this.id ? +this.id : undefined;
  }

  get isValid() {
    return typeof this.id !== 'undefined' && !this.error;
  }

  get isRound() {
    return Boolean(this.cut?.shapeId === 2);
  }

  get isCut() {
    return this.type === 'cut';
  }

  get isCutGrade() {
    return this.type === 'polished-diamond-grade';
  }

  get isFancyColor() {
    return Boolean(this.fancyColor);
  }

  get video360() {
    return this.media?.[0];
  }

  get proportionsImg() {
    return this.reportImg?.SimpleReportColor ?? '';
  }

  get plotActualLengthImg() {
    return this.reportImg?.PlotActualLengthAxisAsym;
  }

  get plotActualWidthImg() {
    return this.reportImg?.PlotActualWidthAxisAsym;
  }

  get plotBothAxesImg() {
    return this.reportImg?.PlotBothAxesSymCor;
  }

  // zero value is same as null for metrics
  get hasAnyCutwiseMetricValue(): boolean {
    return !!this.performance?.integral?.val || !!this.performance?.fire?.val || !!this.performance?.brilliance?.val || !!this.performance?.symmetry?.val;
  }

  get reportImages() {
    const images = [];

    if (this.plotActualLengthImg) {
      images.push({
        url: this.plotActualLengthImg,
        type: 'PlotActualLengthAxisAsym',
      });
    }

    if (this.plotActualWidthImg) {
      images.push({
        url: this.plotActualWidthImg,
        type: 'PlotActualWidthAxisAsym',
      });
    }

    if (this.plotBothAxesImg) {
      images.push({
        url: this.plotBothAxesImg,
        type: 'PlotBothAxesSymCor',
      });
    }

    return images;
  }

  get shapeIntegralHr() {
    if (!this.performance?.integral?.shape?.hr) {
      return '';
    }

    return reverseString(this.performance.integral.shape.hr, '-');
  }

  get shapeIntegralV2Hr() {
    if (!this.performance?.integralV2?.shape?.hr) {
      return '';
    }

    return reverseString(this.performance.integralV2.shape.hr, '-');
  }

  get hasIntegralV2() {
    return Boolean(this.performance?.integralV2?.shape?.hr);
  }

  get plottingDiagram() {
    return this.plottings?.[0]?.svg ?? '';
  }

  get spreadCt() {
    return this.reportData?.spread ?? 0;
  }

  get spreadPc() {
    return this.reportData?.spreadPc ?? 0;
  }

  lightningPreview(type: PhotorealPreviewType) {
    return this.previews?.find(({ lighting }) => lighting === type);
  }
}
